import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  getPickingTasks, createPickingTask, deletePickingTask,
  getPickingAnalytics, getPickingWorkers, getPickingWachtrijen,
  importPickingTasks, bulkDeletePickingTasks, exportPickingTasks,
  exportPickingPerformance,
} from '@/services/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card as TaskCard, CardContent as TaskCardContent, CardHeader as TaskCardHeader, CardTitle as TaskCardTitle } from '@/components/ui/card';
import { DayRangePicker } from '@/components/DayRangePicker';
import { toast } from 'sonner';
import {
  Plus, Trash2, Upload, RefreshCw, Info, LayoutDashboard,
  ClipboardList, Calendar, Camera, Mail,
  GitCompareArrows, X, ChevronDown, FileSpreadsheet, ArrowUp, ArrowDown, ArrowUpDown
} from 'lucide-react';
import html2canvas from 'html2canvas';

import { pickTimeBadge } from './picking/pickingHelpers';
import DashboardTab from './picking/DashboardTab';
import CompareTab from './picking/CompareTab';
import { buildBewerkerMap, anonName, mailtoExport } from './picking/pickingHelpers';

// ========== MAIN PAGE ==========
export const PickingPage = () => {
  const { isAdmin, isGuest, isMasterAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tasks, setTasks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [wachtrijen, setWachtrijen] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(''); // legacy single-select (used by row click in DashboardTab)
  const [selectedWorkers, setSelectedWorkers] = useState([]); // new multi-select
  const [bewerkerPrefix, setBewerkerPrefix] = useState('ALL'); // 'ALL' | 'GRF' | 'GRT'
  const [selectedWachtrijen, setSelectedWachtrijen] = useState([]);
  const [startFrom, setStartFrom] = useState('');
  const [startTo, setStartTo] = useState('');
  const [bevFrom, setBevFrom] = useState('');
  const [bevTo, setBevTo] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [loading, setLoading] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDateFrom, setBulkDateFrom] = useState('');
  const [bulkDateTo, setBulkDateTo] = useState('');
  const [hourlyPopup, setHourlyPopup] = useState(null);
  const [newTask, setNewTask] = useState({});
  const fileInputRef = useRef(null);
  const dashRef = useRef(null);

  const activeFiltersCount = [
    selectedWorker || selectedWorkers.length > 0,
    bewerkerPrefix !== 'ALL',
    selectedWachtrijen.length > 0,
    startFrom || startTo,
    bevFrom || bevTo,
  ].filter(Boolean).length;
  const bewerkerMap = buildBewerkerMap(workers);

  // Workers list filtered by the prefix toggle (GRF / GRT / ALL).
  const workersForSelect = (() => {
    if (bewerkerPrefix === 'GRF') return workers.filter(w => String(w).startsWith('GRF'));
    if (bewerkerPrefix === 'GRT') return workers.filter(w => String(w).startsWith('GRT'));
    return workers;
  })();

  const fmt = (d) => d.toISOString().slice(0, 10);
  const applyPreset = (key) => {
    const now = new Date();
    const today = fmt(now);
    let from = today, to = today;
    if (key === 'today') { from = today; to = today; }
    else if (key === 'yesterday') { const y = new Date(now); y.setDate(y.getDate() - 1); from = fmt(y); to = fmt(y); }
    else if (key === 'last7') { const d = new Date(now); d.setDate(d.getDate() - 6); from = fmt(d); to = today; }
    else if (key === 'last30') { const d = new Date(now); d.setDate(d.getDate() - 29); from = fmt(d); to = today; }
    else if (key === 'thisWeek') { const d = new Date(now); const day = d.getDay(); const diff = day === 0 ? 6 : day - 1; d.setDate(d.getDate() - diff); from = fmt(d); to = today; }
    else if (key === 'thisMonth') { from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`; to = today; }
    setStartFrom(from + 'T00:00');
    setStartTo(to + 'T23:59');
    // Also drive the "Confirmed (day or range)" filter so the Analyze
    // tab — which uses the confirmed range as its source of truth —
    // picks up the same window in one click.
    setBevFrom(from + 'T00:00');
    setBevTo(to + 'T23:59');
    setDatePreset(key);
  };

  const handleStartFrom = (v) => { setStartFrom(v); setDatePreset(''); };
  const handleStartTo = (v) => { setStartTo(v); setDatePreset(''); };

  const hasDateFilter = !!(startFrom || startTo || bevFrom || bevTo);

  // Reference data (workers + wachtrijen) is essentially static — load
  // ONCE on mount, not on every filter change. Previously every date
  // tweak re-fetched both lists, contributing to the intermittent
  // "Failed to load picking data" errors on Citrix.
  useEffect(() => {
    let cancelled = false;
    Promise.all([getPickingWorkers(), getPickingWachtrijen()])
      .then(([w, q]) => {
        if (cancelled) return;
        setWorkers(w);
        setWachtrijen(q);
      })
      .catch((err) => {
        if (cancelled) return;
        // Specific error so the user (and we, via console) know what
        // failed instead of the generic "Failed to load picking data".
        // eslint-disable-next-line no-console
        console.warn('[picking] reference-data load failed', err);
        toast.error(`Could not load workers/queues: ${err.message || 'network'}`);
      });
    return () => { cancelled = true; };
  }, []);

  const loadData = useCallback(async () => {
    // Combined bewerker filter: row-click selectedWorker takes priority,
    // otherwise use multi-select.
    const bewerkerCsv = selectedWorker
      ? selectedWorker
      : (selectedWorkers.length > 0 ? selectedWorkers.join(',') : undefined);
    const filters = {
      bewerker: bewerkerCsv,
      bewerker_prefix: bewerkerCsv ? undefined : (bewerkerPrefix !== 'ALL' ? bewerkerPrefix : undefined),
      wachtrij: selectedWachtrijen.length > 0 ? selectedWachtrijen.join(',') : undefined,
      start_from: startFrom || undefined,
      start_to: startTo || undefined,
      confirmed_from: bevFrom || undefined,
      confirmed_to: bevTo || undefined,
    };
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        setAnalytics(await getPickingAnalytics(filters));
      } else if (!startFrom && !startTo && !bevFrom && !bevTo) {
        setTasks([]);
      } else {
        setTasks(await getPickingTasks(filters));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[picking] data load failed', error);
      toast.error(error?.message || 'Failed to load picking data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedWorker, selectedWorkers, bewerkerPrefix, selectedWachtrijen, startFrom, startTo, bevFrom, bevTo]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    setImportProgress({ phase: 'reading', percent: 0, sentChunks: 0, totalChunks: 0 });
    try {
      const result = await importPickingTasks(file, (info) => {
        // The api helper sends a structured object now {phase, percent, ...}
        // but we keep backward-compat with plain numbers just in case.
        if (typeof info === 'number') setImportProgress((p) => ({ ...p, percent: info }));
        else setImportProgress((p) => ({ ...p, ...info }));
      });
      const replaced = result.replaced_existing || 0;
      const datesTouched = (result.dates_touched || []).length;
      let msg = `Imported ${result.count} tasks`;
      if (replaced) msg += ` (replaced ${replaced} earlier rows across ${datesTouched} date${datesTouched === 1 ? '' : 's'})`;
      toast.success(msg);
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Import failed');
    } finally {
      setImportLoading(false);
      setImportProgress({ phase: 'idle', percent: 0, sentChunks: 0, totalChunks: 0 });
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleteLoading(true);
    try {
      const result = await bulkDeletePickingTasks(bulkDateFrom, bulkDateTo);
      toast.success(result.message || `Deleted ${result.deleted_count} tasks`);
      setBulkDeleteOpen(false);
      setBulkDateFrom('');
      setBulkDateTo('');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Bulk delete failed');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleCreateTask = async () => {
    try {
      await createPickingTask(newTask);
      setCreateOpen(false);
      setNewTask({});
      loadData();
      toast.success('Task added');
    } catch (error) {
      toast.error(error.message || 'Failed to create task');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await deletePickingTask(taskId);
      loadData();
      toast.success('Task deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete');
    }
  };

  const handleHourClick = (hour) => {
    const breakdown = analytics?.hourly_wachtrij_breakdown?.[String(hour)];
    if (breakdown && breakdown.length > 0) {
      setHourlyPopup({ hour, breakdown });
    }
  };

  const handleExportPNG = async () => {
    if (!dashRef.current) return;
    try {
      const canvas = await html2canvas(dashRef.current, { useCORS: true, scale: 2, backgroundColor: '#ffffff' });
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'picking_dashboard.png'; a.click();
        URL.revokeObjectURL(url);
        toast.success('Dashboard exported as PNG');
      });
    } catch {
      toast.error('Failed to export');
    }
  };

  const [perfExportLoading, setPerfExportLoading] = useState(false);
  const handleExportPerformance = async () => {
    const bewerkerCsv = selectedWorker
      ? selectedWorker
      : (selectedWorkers.length > 0 ? selectedWorkers.join(',') : undefined);
    setPerfExportLoading(true);
    try {
      const blob = await exportPickingPerformance({
        bewerker: bewerkerCsv,
        bewerker_prefix: bewerkerCsv ? undefined : (bewerkerPrefix !== 'ALL' ? bewerkerPrefix : undefined),
        wachtrij: selectedWachtrijen.length > 0 ? selectedWachtrijen.join(',') : undefined,
        start_from: startFrom || undefined,
        start_to: startTo || undefined,
        confirmed_from: bevFrom || undefined,
        confirmed_to: bevTo || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bewerker-performance.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Bewerker performance exported');
    } catch (e) {
      toast.error(e?.message || 'Export failed');
    } finally {
      setPerfExportLoading(false);
    }
  };

  const [tasksExportLoading, setTasksExportLoading] = useState(false);
  const [taskSortCol, setTaskSortCol] = useState(null); // 'bewerker'|'wachtrij'|'startloc'|'start'|'confirmed'|'picktime'
  const [taskSortDir, setTaskSortDir] = useState(null); // 'asc'|'desc'|null

  const toggleTaskSort = (col) => {
    if (taskSortCol !== col) { setTaskSortCol(col); setTaskSortDir('asc'); }
    else if (taskSortDir === 'asc') setTaskSortDir('desc');
    else { setTaskSortCol(null); setTaskSortDir(null); }
  };

  const sortedTasks = (() => {
    if (!taskSortCol || !taskSortDir) return tasks;
    const getVal = (t) => {
      switch (taskSortCol) {
        case 'bewerker': return (t.Bewerker || '').toLowerCase();
        case 'wachtrij': return (t.Wachtrij || '').toLowerCase();
        case 'startloc': return (t['Startloc.'] || '').toLowerCase();
        case 'start': return t.start_datetime || '';
        case 'confirmed': return t.end_datetime || '';
        case 'picktime': return Number(t.pick_time_min) || 0;
        default: return '';
      }
    };
    return [...tasks].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (typeof va === 'number' && typeof vb === 'number') {
        return taskSortDir === 'asc' ? va - vb : vb - va;
      }
      return taskSortDir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
  })();

  const SortBtn = ({ col, label, align = 'left' }) => {
    const active = taskSortCol === col;
    const Icon = active ? (taskSortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <button
        type="button"
        onClick={() => toggleTaskSort(col)}
        className={`inline-flex items-center gap-1 font-medium text-muted-foreground hover:text-foreground transition-colors px-2 py-1 -mx-2 rounded hover:bg-muted/50 ${active ? 'text-foreground bg-muted/60' : ''} ${align === 'right' ? 'flex-row-reverse' : ''}`}
        data-testid={`task-sort-${col}`}
      >
        <span>{label}</span>
        <Icon className={`h-3 w-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    );
  };
  const handleExportTasksExcel = async () => {
    if (!hasDateFilter) {
      toast.error('Select a date range first');
      return;
    }
    setTasksExportLoading(true);
    try {
      const blob = await exportPickingTasks({
        bewerker: selectedWorker || undefined,
        wachtrij: selectedWachtrijen.length > 0 ? selectedWachtrijen.join(',') : undefined,
        start_from: startFrom || undefined,
        start_to: startTo || undefined,
        bevestiging_from: bevFrom || undefined,
        bevestiging_to: bevTo || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'picking_tasks.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Tasks exported as Excel');
    } catch {
      toast.error('Export failed');
    } finally {
      setTasksExportLoading(false);
    }
  };

  const MANUAL_FIELDS = [
    { key: 'Voorraadorder', label: 'Voorraadorder', type: 'text' },
    { key: 'Type aanm.regel', label: 'Type aanm.regel', type: 'text' },
    { key: 'Wachtrij', label: 'Wachtrij', type: 'text' },
    { key: 'Bewerker', label: 'Bewerker', type: 'text', required: true },
    { key: 'Begindatum', label: 'Begindatum', type: 'date' },
    { key: 'Starttijd', label: 'Starttijd', type: 'time' },
    { key: 'Datum bevestiging', label: 'Datum bevestiging', type: 'date' },
    { key: 'Bevestigingstijd', label: 'Bevestigingstijd', type: 'time' },
    { key: 'Gecreëerd door', label: 'Gecreëerd door', type: 'text' },
    { key: 'Startloc.', label: 'Startloc.', type: 'text' },
  ];

  const formatDt = (val) => {
    if (!val) return '-';
    try {
      const d = new Date(val);
      return d.toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' });
    } catch { return val; }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="picking-page">
        {isGuest && (
          <div className="flex items-center gap-3 px-4 py-3 bg-sky-50 border border-sky-200 rounded-lg" data-testid="demo-data-banner">
            <Info className="h-5 w-5 text-sky-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-sky-800">Sample Data Preview</p>
              <p className="text-xs text-sky-600">Illustrative picking tasks shown below.</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Order Picking</h1>
            <p className="text-muted-foreground mt-1">Analyze picking performance and productivity</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={loadData} data-testid="refresh-picking">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            {activeTab === 'dashboard' && (
              <>
                <Button variant="outline" size="sm" onClick={handleExportPerformance} disabled={perfExportLoading} data-testid="export-bewerker-performance">
                  <FileSpreadsheet className="h-4 w-4 mr-2" /> {perfExportLoading ? 'Exporting…' : 'Export Excel'}
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportPNG} data-testid="export-picking-png">
                  <Camera className="h-4 w-4 mr-2" /> PNG
                </Button>
                <Button variant="ghost" size="sm" onClick={() => mailtoExport('picking_dashboard.png')} data-testid="mailto-picking-btn">
                  <Mail className="h-4 w-4 mr-2" /> Email
                </Button>
              </>
            )}
            {!isGuest && (
              <>
                <input
                  type="file" ref={fileInputRef} accept=".xlsx,.xls"
                  onChange={handleImport} className="hidden" data-testid="picking-import-input"
                />
                <div className="relative">
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importLoading} data-testid="picking-import-btn">
                    <Upload className="h-4 w-4 mr-2" />
                    {importLoading ? (
                      <span className="tabular-nums">
                        {importProgress.phase === 'reading' && 'Reading file…'}
                        {importProgress.phase === 'uploading' && (
                          <>Uploading {importProgress.percent}%
                            {importProgress.totalChunks ? ` · ${importProgress.sentChunks}/${importProgress.totalChunks} chunks` : ''}
                          </>
                        )}
                        {importProgress.phase === 'processing' && (
                          importProgress.serverPhase
                            ? `${importProgress.serverPhase} ${importProgress.serverProgress != null ? `${importProgress.serverProgress}%` : ''}`
                            : 'Processing on server…'
                        )}
                        {importProgress.phase === 'done' && 'Finishing…'}
                        {importProgress.phase === 'idle' && 'Starting…'}
                      </span>
                    ) : 'Import Excel'}
                  </Button>
                  {importLoading && (
                    <div
                      className="absolute left-0 right-0 -bottom-1 h-1 bg-muted rounded-full overflow-hidden"
                      data-testid="picking-import-progress-bar"
                    >
                      <div
                        className="h-full bg-cyan-500 transition-all duration-200"
                        style={{ width: `${importProgress.percent}%` }}
                      />
                    </div>
                  )}
                </div>
                <Button onClick={() => setCreateOpen(true)} data-testid="picking-new-task-btn">
                  <Plus className="h-4 w-4 mr-2" /> Manual Entry
                </Button>
                {isMasterAdmin && (
                  <Button variant="destructive" onClick={() => setBulkDeleteOpen(true)} data-testid="picking-bulk-delete-btn">
                    <Trash2 className="h-4 w-4 mr-2" /> Bulk Delete
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="border-dashed">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Bewerker</Label>
                <div className="flex items-center gap-1.5">
                  {['ALL', 'GRF', 'GRT'].map(p => (
                    <Button
                      key={p}
                      type="button"
                      size="sm"
                      variant={bewerkerPrefix === p ? 'default' : 'outline'}
                      className="h-9 px-3 text-xs"
                      onClick={() => { setBewerkerPrefix(p); setSelectedWorker(''); setSelectedWorkers([]); }}
                      data-testid={`bewerker-prefix-${p.toLowerCase()}`}
                    >
                      {p === 'ALL' ? 'All' : p}
                    </Button>
                  ))}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 h-9 justify-between text-sm font-normal" data-testid="bewerker-multi-filter">
                        {selectedWorkers.length === 0
                          ? (bewerkerPrefix === 'ALL' ? 'All workers' : `${bewerkerPrefix}xxx`)
                          : selectedWorkers.length === 1
                            ? anonName(selectedWorkers[0], bewerkerMap, isGuest)
                            : `${selectedWorkers.length} selected`}
                        <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2 max-h-80 overflow-y-auto" align="start">
                      {workersForSelect.length === 0 ? (
                        <p className="text-xs text-muted-foreground px-2 py-2">No workers</p>
                      ) : workersForSelect.map(w => (
                        <label key={w} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer text-sm">
                          <Checkbox
                            checked={selectedWorkers.includes(w)}
                            onCheckedChange={(checked) => {
                              setSelectedWorker('');
                              setSelectedWorkers(prev => checked ? [...prev, w] : prev.filter(v => v !== w));
                            }}
                            data-testid={`bewerker-opt-${w}`}
                          />
                          {anonName(w, bewerkerMap, isGuest)}
                        </label>
                      ))}
                      {selectedWorkers.length > 0 && (
                        <Button variant="ghost" size="sm" className="w-full mt-1 text-xs" onClick={() => setSelectedWorkers([])} data-testid="bewerker-clear">
                          Clear selection
                        </Button>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Wachtrij</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-48 h-9 justify-between text-sm font-normal" data-testid="wachtrij-filter">
                      {selectedWachtrijen.length === 0 ? 'All' : selectedWachtrijen.length === 1 ? selectedWachtrijen[0] : `${selectedWachtrijen.length} selected`}
                      <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-2" align="start">
                    {wachtrijen.map(w => (
                      <label key={w} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer text-sm">
                        <Checkbox
                          checked={selectedWachtrijen.includes(w)}
                          onCheckedChange={(checked) => {
                            setSelectedWachtrijen(prev => checked ? [...prev, w] : prev.filter(v => v !== w));
                          }}
                          data-testid={`wachtrij-opt-${w}`}
                        />
                        {w}
                      </label>
                    ))}
                    {selectedWachtrijen.length > 0 && (
                      <Button variant="ghost" size="sm" className="w-full mt-1 text-xs" onClick={() => setSelectedWachtrijen([])}>
                        Clear selection
                      </Button>
                    )}
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Confirmed (day or range)</Label>
                <DayRangePicker
                  fromIso={bevFrom}
                  toIso={bevTo}
                  onChange={(f, t) => { setBevFrom(f); setBevTo(t); }}
                  testIdPrefix="bev-range"
                />
              </div>

              {activeFiltersCount > 0 && (
                <Button variant="ghost" size="sm" className="h-9 text-xs text-muted-foreground" data-testid="clear-filters-btn"
                  onClick={() => { setSelectedWorker(''); setSelectedWorkers([]); setBewerkerPrefix('ALL'); setSelectedWachtrijen([]); setStartFrom(''); setStartTo(''); setBevFrom(''); setBevTo(''); setDatePreset(''); }}>
                  <X className="h-3.5 w-3.5 mr-1" /> Clear {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-dashed">
              <span className="text-xs text-muted-foreground mr-1">Quick:</span>
              {[
                { key: 'today', label: 'Today' },
                { key: 'yesterday', label: 'Yesterday' },
                { key: 'thisWeek', label: 'This Week' },
                { key: 'last7', label: 'Last 7 Days' },
                { key: 'thisMonth', label: 'This Month' },
                { key: 'last30', label: 'Last 30 Days' },
              ].map(p => (
                <Button
                  key={p.key}
                  variant={datePreset === p.key ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs px-3"
                  onClick={() => applyPreset(p.key)}
                  data-testid={`preset-${p.key}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="dashboard" data-testid="tab-pk-dashboard">
              <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="compare" data-testid="tab-pk-analyze">
              <GitCompareArrows className="h-4 w-4 mr-2" /> Analyze
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-6">
            <DashboardTab
              analytics={analytics} loading={loading}
              selectedWorker={selectedWorker} workers={workers}
              onWorkerChange={setSelectedWorker} dashRef={dashRef}
              onHourClick={handleHourClick} isGuest={isGuest}
            />
          </TabsContent>

          <TabsContent value="compare" className="mt-6">
            <CompareTab
              workers={workers}
              isGuest={isGuest}
              bevFrom={bevFrom}
              bevTo={bevTo}
              bewerkerPrefix={bewerkerPrefix}
              selectedWorkers={selectedWorkers}
              selectedWachtrijen={selectedWachtrijen}
            />
          </TabsContent>
        </Tabs>

        {/* Manual Entry Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-[480px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle data-testid="create-task-dialog">Manual Picking Entry</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              {MANUAL_FIELDS.map(f => (
                <div key={f.key} className="flex items-center gap-3">
                  <Label className="text-xs w-36 flex-shrink-0">
                    {f.label}
                    {f.required && <span className="text-rose-500 ml-0.5">*</span>}
                  </Label>
                  <Input
                    type={f.type}
                    value={newTask[f.key] || ''}
                    onChange={(e) => setNewTask(p => ({...p, [f.key]: e.target.value}))}
                    className="h-9 text-sm flex-1"
                    data-testid={`manual-${f.key}`}
                  />
                </div>
              ))}
              <Button
                onClick={handleCreateTask}
                className="w-full mt-2"
                disabled={!newTask['Bewerker']?.trim()}
                data-testid="save-picking-task"
              >
                Save Task
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Hourly Wachtrij Breakdown Popup */}
        <Dialog open={!!hourlyPopup} onOpenChange={() => setHourlyPopup(null)}>
          <DialogContent className="sm:max-w-[380px]" data-testid="hourly-wachtrij-popup">
            <DialogHeader>
              <DialogTitle>Wachtrij Breakdown — {hourlyPopup?.hour}:00</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 pt-2">
              {hourlyPopup?.breakdown?.map(item => (
                <div key={item.wachtrij} className="flex items-center justify-between p-2.5 bg-muted/30 rounded-lg">
                  <span className="font-medium text-sm">{item.wachtrij}</span>
                  <div className="text-right">
                    <span className="font-mono text-sm font-semibold">{item.avg}</span>
                    <span className="text-xs text-muted-foreground ml-1">avg/day</span>
                    <span className="text-xs text-muted-foreground ml-2">({item.count} total)</span>
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Dialog */}
        <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
          <DialogContent className="sm:max-w-[420px]">
            <DialogHeader>
              <DialogTitle data-testid="bulk-delete-dialog-title">Bulk Delete Imported Data</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                This will permanently delete all <strong>imported</strong> picking tasks. Optionally filter by date range.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">From (optional)</Label>
                  <Input
                    type="date"
                    value={bulkDateFrom}
                    onChange={(e) => setBulkDateFrom(e.target.value)}
                    data-testid="bulk-delete-from"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">To (optional)</Label>
                  <Input
                    type="date"
                    value={bulkDateTo}
                    onChange={(e) => setBulkDateTo(e.target.value)}
                    data-testid="bulk-delete-to"
                  />
                </div>
              </div>
              <p className="text-xs text-rose-500 font-medium">
                {bulkDateFrom || bulkDateTo
                  ? `Deleting imported tasks from ${bulkDateFrom || 'start'} to ${bulkDateTo || 'end'}`
                  : 'No date range — all imported tasks will be deleted'}
              </p>
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} data-testid="bulk-delete-cancel">
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteLoading}
                  data-testid="bulk-delete-confirm"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {bulkDeleteLoading ? 'Deleting...' : 'Delete Imported Data'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
};

export default PickingPage;
